import { useState, useEffect } from "react";
import axios from "axios";
import jwt from "jsonwebtoken";
import { NextRouter, useRouter } from "next/router";
import { CURRENCY_SYMBOLS, BaseShopifyShippingAddress, LoopDiscountType } from "./constants";
import Bugsnag from "@bugsnag/js";
import {
    fetchTranslationDataVariant,
    setTranslationData,
    setTranslationDataVariant,
    translateDateEpoch,
} from "./translations";
import { fetchTranslationData } from "./translations";
import { TRANSFORMATION_TYPES } from "./constants";
import useNavigationURL from "../hooks/useNavigationURL";

export function validateEmail(email: string) {
    const emailRegex = /^[a-zA-Z0-9._\-+!#$%&*^~'\/=\[\]]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
}

export const safeStorage = {
    getItem(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch (e) {
            return null;
        }
    },

    setItem(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch (e) {
            // Do nothing
        }
    },

    removeItem(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            // Do nothing
        }
    },
};

export function useAxios() {
    const router = useRouter();
    const instance = axios.create();
    const navigationUrl = useNavigationURL();

    instance.defaults.timeout = 30 * 1000;

    instance.interceptors.request.use(function (config) {
        let _token = safeStorage.getItem(`loopAccessToken`);
        if (router.query.merchantToken) {
            _token = router.query.merchantToken as string;
        }
        config.headers.Authorization = _token ? `Bearer ${_token}` : "";
        return config;
    });

    instance.interceptors.response.use(
        function (response) {
            return response;
        },
        async function (error) {
            let _token = safeStorage.getItem(`loopAccessToken`);
            if (router.query.merchantToken) {
                _token = router.query.merchantToken as string;
            }

            const _parsedAccessToken = parseJwt(_token);
            if (_parsedAccessToken) {
                if (error?.response?.status == 401) {
                    const action = "customer";
                    return router.push(`${navigationUrl}/login?action=${action}&customer_id=${_parsedAccessToken.userShopifyId}`);
                }
            }

            return Promise.reject(error);
        },
    );

    return [instance];
}

type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
interface JsonObject {
    [key: string]: JsonValue;
}
interface JsonArray extends Array<JsonValue> { }

function toCamelCase(str: string): string {
    return str.replace(/([-_][a-z])/g, (group: string) => group.toUpperCase().replace("-", "").replace("_", ""));
}

export function convertToCamelCase(obj: JsonValue): JsonValue {
    if (Array.isArray(obj)) {
        return obj.map((v) => convertToCamelCase(v));
    } else if (obj !== null && typeof obj === "object") {
        return Object.keys(obj).reduce<JsonObject>((result, key) => {
            const value = obj[key];
            const newKey = toCamelCase(key);
            result[newKey] = convertToCamelCase(value);
            return result;
        }, {});
    }
    return obj;
}

export function getDateStrFromEpoch(epoch: number) {
    return new Date(Number(epoch) * 1000).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

export function getDateStrFromStr(dateStr: string, showInternationalDates: boolean, locale: string, country: string) {
    let finalLocale = "en-US";
    if (showInternationalDates) {
        finalLocale = locale.split("-")[0] + `-` + country;
    }
    return new Date(dateStr).toLocaleString(finalLocale, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

export function getTranslatedDateStrFromStr(
    dateStr: string,
    showInternationalDates: boolean,
    locale: string,
    country: string,
) {
    const date = new Date(dateStr);
    return translateDateEpoch(Math.ceil(date.getTime()) / 1000, showInternationalDates, locale, country);
}

export function handleError(e: any, fileName: string, functionName: string, data: any) {
    if (e.response?.status == 401) {
        return;
    }

    Bugsnag.notify(e, function (event) {
        event.addMetadata("data", {
            fileName: fileName,
            functionName: functionName,
            data: data,
            source: "LOOP",
        });
    });
}

export function getCurrencyDisplay(code: string) {
    if (code in CURRENCY_SYMBOLS) {
        return CURRENCY_SYMBOLS[code];
    } else {
        return code + " ";
    }
}

export function getCurrencyDisplayInternationalized(
    currencyCode: string,
    locale: string,
    country: string,
    amount: number,
) {
    try {
        if (locale && country) {
            let finalLocale = locale.split("-")[0] + `-` + country;
            return new Intl.NumberFormat(finalLocale, {
                style: "currency",
                currency: `${currencyCode}`,
                maximumFractionDigits: 2,
            }).format(amount);
        }
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: `${currencyCode}`,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch (error) {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: `USD`,
            maximumFractionDigits: 2,
        }).format(amount);
    }
}

export function getVariantTitleDisplay(title: any) {
    return title != "" && title != "Default Title" && title != null ? title : "";
}

export function calcDiscountDisplayFromPP(discounts: any, billing_cycles: any, currencySymb: string) {
    const sorted_cycleDiscounts = discounts.sort(function (a: any, b: any) {
        return a.afterCycle - b.afterCycle;
    });
    let finalDisc: any = {};
    for (let disc of sorted_cycleDiscounts) {
        if (billing_cycles >= disc.afterCycle) {
            finalDisc = { ...disc };
        }
    }
    if (finalDisc.adjustmentType == "PERCENTAGE") {
        if (parseFloat(finalDisc.adjustmentValue.percentage) != 0) {
            return Math.round(skipTrailingZeroes(parseFloat(finalDisc.adjustmentValue.percentage), 2)) + "%";
        }
    } else if (finalDisc.adjustmentType == "FIXED_AMOUNT") {
        if (parseFloat(finalDisc.adjustmentValue.amount) != 0) {
            return currencySymb + "" + parseFloat(finalDisc.adjustmentValue.amount).toFixed(2);
        }
    } else if (finalDisc.adjustmentType == "PRICE") {
        // if (parseFloat(finalDisc.adjustmentValue.amount) != 0) {
        //   return "Subscription discount (" + currencySymb + "" + parseFloat(finalDisc.adjustmentValue.amount).toFixed(2) + ")"
        // }
        return null;
    }
    return null;
}

export function calcDaysForInterval(obj: any) {
    if (obj.billing_interval.toLowerCase() == "day") {
        return 1 * parseInt(obj.billing_interval_count);
    } else if (obj.billing_interval.toLowerCase() == "week") {
        return 7 * parseInt(obj.billing_interval_count);
    } else if (obj.billing_interval.toLowerCase() == "month") {
        return 30 * parseInt(obj.billing_interval_count);
    } else if (obj.billing_interval.toLowerCase() == "year") {
        return 365 * parseInt(obj.billing_interval_count);
    }
}

export function calcDiscountValue(basePrice: any, discounts: any, billing_cycles: number) {
    let newPrice = parseFloat(basePrice);
    const sorted_cycleDiscounts = discounts.sort(function (a: any, b: any) {
        return a.afterCycle - b.afterCycle;
    });
    for (let disc of sorted_cycleDiscounts) {
        if (billing_cycles >= disc.afterCycle) {
            newPrice =
                disc.adjustmentType == "PERCENTAGE"
                    ? parseFloat(basePrice) * (1 - disc.adjustmentValue.percentage / 100)
                    : disc.adjustmentType == "PRICE"
                        ? parseFloat(disc.adjustmentValue.amount)
                        : parseFloat(basePrice) - parseFloat(disc.adjustmentValue.amount);
        }
    }
    return newPrice;
}

export function calcDiscountSp(pricing_policy: any, billing_cycles: number, currencySymb: string) {
    let _str = "";
    if (!pricing_policy) {
        return "";
    }
    let finalDisc: any = {};
    for (let disc of pricing_policy) {
        if (disc.__typename == "SellingPlanFixedPricingPolicy") {
            finalDisc = { ...disc };
        }
        if (disc.__typename == "SellingPlanRecurringPricingPolicy") {
            if (billing_cycles >= disc.afterCycle) {
                finalDisc = { ...disc };
            }
        }
    }
    if (finalDisc.adjustmentType == "PERCENTAGE") {
        if (finalDisc.adjustmentValue.percentage == 0) {
            return "";
        }
        _str = Math.round(skipTrailingZeroes(finalDisc.adjustmentValue.percentage, 2)) + "%";
    } else if (finalDisc.adjustmentType == "FIXED_AMOUNT") {
        if (parseFloat(finalDisc.adjustmentValue.amount) == 0) {
            return "";
        }
        _str = currencySymb + "" + parseFloat(finalDisc.adjustmentValue.amount).toFixed(2);
    } else if (finalDisc.adjustmentType == "PRICE") {
        if (parseFloat(finalDisc.adjustmentValue.amount) == 0) {
            return "";
        }
        _str = "";
    }
    return _str;
}

export function calcDiscountSpValue(pricing_policy: any, billing_cycles: number, base_price: any) {
    if (!pricing_policy) {
        return parseFloat(base_price).toFixed(2);
    }
    if (pricing_policy.length == 0) {
        return parseFloat(base_price).toFixed(2);
    }
    let finalDisc: any = {};
    for (let disc of pricing_policy) {
        if (disc.__typename == "SellingPlanFixedPricingPolicy") {
            finalDisc = { ...disc };
        }
        if (disc.__typename == "SellingPlanRecurringPricingPolicy") {
            if (billing_cycles >= disc.afterCycle) {
                finalDisc = { ...disc };
            }
        }
    }
    if (finalDisc.adjustmentType == "PERCENTAGE") {
        if (finalDisc.adjustmentValue.percentage == 0) {
            return parseFloat(base_price).toFixed(2);
        }
        return skipTrailingZeroes(parseFloat(base_price) * (1 - finalDisc.adjustmentValue.percentage / 100), 2);
    } else if (finalDisc.adjustmentType == "FIXED_AMOUNT") {
        if (parseFloat(finalDisc.adjustmentValue.amount) == 0) {
            return parseFloat(base_price).toFixed(2);
        }
        return (parseFloat(base_price) - finalDisc.adjustmentValue.amount).toFixed(2);
    } else if (finalDisc.adjustmentType == "PRICE") {
        return parseFloat(finalDisc.adjustmentValue.amount).toFixed(2);
    }
}

export function calcDiscountSpString(pricing_policy: any, billing_cycles: number, currencySymb: string) {
    if (!pricing_policy) {
        return null;
    }
    if (pricing_policy.length == 0) {
        return null;
    }
    let finalDisc: any = {};
    for (let disc of pricing_policy) {
        if (disc.__typename == "SellingPlanFixedPricingPolicy") {
            finalDisc = { ...disc };
        }
        if (disc.__typename == "SellingPlanRecurringPricingPolicy") {
            if (billing_cycles >= disc.afterCycle) {
                finalDisc = { ...disc };
            }
        }
    }
    if (finalDisc.adjustmentType == "PERCENTAGE") {
        if (finalDisc.adjustmentValue.percentage == 0) {
            return null;
        }
        return {
            type: "PERCENTAGE",
            value: skipTrailingZeroes(finalDisc.adjustmentValue.percentage, 2) + "%",
        };
    } else if (finalDisc.adjustmentType == "FIXED_AMOUNT") {
        if (parseFloat(finalDisc.adjustmentValue.amount) == 0) {
            return null;
        }
        return {
            type: "FIXED_AMOUNT",
            value: currencySymb + parseFloat(finalDisc.adjustmentValue.amount).toFixed(2),
        };
    } else if (finalDisc.adjustmentType == "PRICE") {
        return {
            type: "PRICE",
            value: currencySymb + parseFloat(finalDisc.adjustmentValue.amount).toFixed(2),
        };
    }
}

export function loopSAtoShopify(shAdd: any) {
    let base = { ...BaseShopifyShippingAddress };
    if (shAdd) {
        base.firstName = shAdd.first_name || "";
        base.lastName = shAdd.last_name || "";
        base.address1 = shAdd.address1 || "";
        base.address2 = shAdd.address2 || "";
        base.phone = shAdd.phone || "";
        base.company = shAdd.company || "";
        base.city = shAdd.city || "";
        base.zip = shAdd.zip || "";
        base.countryCode = shAdd.country_code || "";
        base.provinceCode = shAdd.province_code || "";
    }
    return base;
}

export function loopLocalDeliveryToShopify(shAdd: any) {
    if (shAdd) {
        return {
            firstName: shAdd.firstName || "",
            lastName: shAdd.lastName || "",
            address1: shAdd.address1 || "",
            address2: shAdd.address2 || "",
            phone: shAdd.phone || "",
            company: shAdd.company || "",
            city: shAdd.city || "",
            zip: shAdd.zip || "",
            countryCode: shAdd.countryCode || "",
            provinceCode: shAdd.provinceCode || "",
        };
    }
}

export function loopSAtoString(shAdd: any) {
    let _str = "";
    if (shAdd) {
        _str += shAdd.address1 ? shAdd.address1 : "";
        _str += shAdd.address2 ? ", " + shAdd.address2 : "";
        _str += shAdd.city ? ", " + shAdd.city : "";
        _str += shAdd.zip ? ", " + shAdd.zip : "";
        _str += shAdd.province_code ? ", " + shAdd.province_code : "";
        _str += shAdd.country_code ? ", " + shAdd.country_code : "";
    }
    return _str;
}

export function getVariantTitle(options: Array<string>) {
    return options.join(" / ");
}

export function generateUEID() {
    let first: any = (Math.random() * 46656) | 0;
    let second: any = (Math.random() * 46656) | 0;
    first = ("000" + first.toString(36)).slice(-3);
    second = ("000" + second.toString(36)).slice(-3);

    return first + second;
}

export function capitalizeFirstLetter(_str: string) {
    return _str.charAt(0).toUpperCase() + _str.slice(1);
}

export function generateBuildABoxToken({ shop, customerId, bundleTransactionId }) {
    const token = jwt.sign({ shop, customerId, bundleTransactionId }, process.env.NEXT_PUBLIC_BUILDABOX_KEY, {
        expiresIn: "2 days",
    });
    return token;
}

export function truncateText(str: string, length: number) {
    if (str.length < length) {
        return str;
    }
    return str.slice(0, length) + "...";
}

export function getPaths(path: string) {
    let _proxyPath = process.env.NEXT_PUBLIC_PROXY_PATH.slice(0, -1);
    const _pathArr = path.split(_proxyPath);
    let preProxy = "";
    let postProxy = "";
    if (_pathArr.length > 1) {
        preProxy = _pathArr[_pathArr.length - 2];
        postProxy = _pathArr[_pathArr.length - 1]; //_pathArr.join(process.env.NEXT_PUBLIC_PROXY_PATH)
    } else {
        postProxy = _pathArr[0];
    }
    return {
        preProxy: preProxy,
        proxy: _proxyPath,
        postProxy: postProxy,
    };
}

export function extractLocale(preProxyPath: string) {
    const _preProxyPath = preProxyPath.replace("/", "");
    return _preProxyPath.split("-")[0];
}

export function skipTrailingZeroes(val: number, base: number) {
    let _base = 10 ** base;
    return Math.round(val * _base) / _base;
}

export function getDiscountAmount(basePrice: number, type: string, value: number): number {
    if (type == "PERCENTAGE") {
        return basePrice * (1 - value / 100);
    } else if (type == "FIXED_AMOUNT") {
        return basePrice - value;
    } else if (type == "PRICE") {
        return value;
    } else if (type == "BASED_ON_SELLING_PLAN") {
        return value;
    }
    return basePrice;
}

export function parseJwt(token: string) {
    if (!token) {
        return null;
    }
    try {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace("-", "+").replace("_", "/");
        return JSON.parse(window.atob(base64));
    } catch (error) {
        return null;
    }
}

export function dbAddressToGraphQl(address: any) {
    return {
        firstName: address.firstName,
        lastName: address.lastName,
        company: address.company ?? "",
        address1: address.address1,
        address2: address.address2,
        zip: address.zip,
        city: address.city,
        country: address.country,
        countryCode: address.countryCode,
        province: address.province,
        provinceCode: address.provinceCode,
    };
}

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
        width,
        height,
    };
}

export function useWindowDimensions() {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return windowDimensions;
}

export const kebabize = (str: string) =>
    str.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? "-" : "") + $.toLowerCase());

export function setWindowVariables(newData: any) {
    const _currentProps = (window as any).loopProps || {};
    (window as any).loopProps = { ..._currentProps, ...newData };
}

export const sortItemsByKey = (items, key) => {
    return items.sort((a, b) => {
        let titleA = a[key].toUpperCase();
        let titleB = b[key].toUpperCase();
        if (titleA < titleB) {
            return -1;
        }
        if (titleA > titleB) {
            return 1;
        }
        return 0;
    });
};

export function checkIfActionValidOnPrepaidV2(isContractPrepaid, featureKey: string, featureSwitches) {
    if (!isContractPrepaid) return true;

    return featureSwitches.includes(featureKey);
}

// The following function is used to emit custom events
export const emitCustomEvent = (eventName: string) => {
    const event = new CustomEvent(eventName);
    window.dispatchEvent(event);
};

// Multilingial Translation Helpers
export async function transformMultilingualData(
    data: any,
    shopifyDomain: string,
    locale: string,
    storeDefaultLocale: string | null,
    localeRoute: string,
) {
    try {
        const res = await handleMultilingualData(data, shopifyDomain, locale, storeDefaultLocale, localeRoute);
        for (let i = 0; i < data.length; i++) {
            if (!data[i]?.isBundle) {
                data[i].product_title = res[i]?.title;
            } else {
                if (storeDefaultLocale === locale) locale = null;
                let productHandle = data[i]?.product_handle;

                if (locale && productHandle && data[i]?.isPreset) {
                    const url = `https://${shopifyDomain}/${locale.split("-")[0]}/products/${productHandle}`;
                    const response = await axios.get(url, {
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    });
                    if (!response?.data) return data;
                    data[i].product_title = response.data.product?.title;
                    const lenVariants = response.data.product?.options.length;
                    const prod_len = data[i]?.product_options?.length;

                    // null check for product options its null at some places
                    if (prod_len) {
                        for (let j = 0; j < prod_len; j++) {
                            for (let k = 0; k < lenVariants; k++) {
                                data[i].product_options[j].values[k] = response.data.product.options[j].values[k];
                            }
                        }
                    }
                } else if (locale && !data[i].isPreset) {
                    data[i].product_title =
                        (await titleTranslationBundle(locale, data[i].bundleId, shopifyDomain)) ||
                        data[i].product_title;
                }
            }
        }
        return data;
    } catch (error) {
        return data;
    }
}

export async function transformMultilingualDataForLineItems(
    data: any,
    shopifyDomain: string,
    locale: string,
    storeDefaultLocale: string | null,
    localeRoute: string,
) {
    try {
        for (let i = 0; i < data.length; i++) {
            for (let j = 0; j < data[i].line_items.length; j++) {
                if (!data[i].line_items[j]?.isBundleItems) {
                    data[i].line_items[j].title =
                        (await titleTranslation(
                            storeDefaultLocale,
                            shopifyDomain,
                            locale,
                            data[i].line_items[j].product_handle,
                            localeRoute,
                        )) || data[i].line_items[j].title;
                    if (data[i].line_items[j]?.product_handle)
                        data[i].line_items[j].variant_title =
                            (await variantTranslation(
                                storeDefaultLocale,
                                shopifyDomain,
                                locale,
                                data[i].line_items[j].product_handle,
                                data[i].line_items[j].variant_title,
                                localeRoute,
                            )) || data[i].line_items[j].variant_title;
                } else if (data[i].line_items[j]?.isPresetBundle) {
                    data[i].line_items[j].title =
                        (await titleTranslation(
                            storeDefaultLocale,
                            shopifyDomain,
                            locale,
                            data[i].line_items[j].product_handle,
                            localeRoute,
                        )) || data[i].line_items[j].title;
                    if (data[i].line_items[j]?.product_handle)
                        data[i].line_items[j].variant_title =
                            (await variantTranslation(
                                storeDefaultLocale,
                                shopifyDomain,
                                locale,
                                data[i].line_items[j].product_handle,
                                data[i].line_items[j].variant_title,
                                localeRoute,
                            )) || data[i].line_items[j].variant_title;
                } else {
                    data[i].line_items[j].title =
                        (await titleTranslationBundle(locale, data[i].line_items[j].loopBundleId, shopifyDomain)) ||
                        data[i].line_items[j].title;
                }
            }
        }
    } catch (error) {
        return data;
    }
}

export async function transformMultilingualDataForLineItemsForBundle(
    data: any,
    shopifyDomain: string,
    locale: string,
    storeDefaultLocale: string | null,
    localeRoute: string,
) {
    try {
        if (locale == storeDefaultLocale) return data;
        for (let i = 0; i < data.length; i++) {
            if (data[i]?.isBundleItems) {
                if (data[i]?.isPresetBundle) {
                    const title = data[i].title;
                    const variant_title = data[i].variant_title;
                    data[i].title =
                        (await titleTranslation(
                            storeDefaultLocale,
                            shopifyDomain,
                            locale,
                            data[i].product_handle,
                            localeRoute,
                        )) || title;
                    data[i].variant_title =
                        (await variantTranslation(
                            storeDefaultLocale,
                            shopifyDomain,
                            locale,
                            data[i].product_handle,
                            variant_title,
                            localeRoute,
                        )) || variant_title;
                    for (let j = 0; j < data[i].bundleItems.length; j++) {
                        data[i].bundleItems[j].title =
                            (await titleTranslation(
                                storeDefaultLocale,
                                shopifyDomain,
                                locale,
                                data[i].bundleItems[j].product_handle,
                                localeRoute,
                            )) || data[i].bundleItems[j].title;
                        data[i].bundleItems[j].variant_title =
                            (await variantTranslation(
                                storeDefaultLocale,
                                shopifyDomain,
                                locale,
                                data[i].bundleItems[j].product_handle,
                                data[i].bundleItems[j].variant_title,
                                localeRoute,
                            )) || data[i].bundleItems[j].variant_title;
                    }
                } else {
                    data[i].title = await titleTranslationBundle(locale, data[i].loopBundleId, shopifyDomain);
                    for (let j = 0; j < data[i].bundleItems.length; j++) {
                        data[i].bundleItems[j].title =
                            (await titleTranslation(
                                storeDefaultLocale,
                                shopifyDomain,
                                locale,
                                data[i].bundleItems[j].product_handle,
                                localeRoute,
                            )) || data[i].bundleItems[j].title;
                        data[i].bundleItems[j].variant_title =
                            (await variantTranslation(
                                storeDefaultLocale,
                                shopifyDomain,
                                locale,
                                data[i].bundleItems[j].product_handle,
                                data[i].bundleItems[j].variant_title,
                                localeRoute,
                            )) || data[i].bundleItems[j].variant_title;
                    }
                }
            } else {
                data[i].title =
                    (await titleTranslation(
                        storeDefaultLocale,
                        shopifyDomain,
                        locale,
                        data[i].product_handle,
                        localeRoute,
                    )) || data[i].title;
                data[i].variant_title =
                    (await variantTranslation(
                        storeDefaultLocale,
                        shopifyDomain,
                        locale,
                        data[i]?.product_handle,
                        data[i]?.variant_title,
                        localeRoute,
                    )) || data[i]?.variant_title;
            }
        }
        return data;
    } catch (error) {
        return data;
    }
}

export async function transformMultilingualDataForGiftLineItems(
    lineItems: any,
    shopifyDomain: string,
    locale: string,
    storeDefaultLocale: string | null,
    localeRoute: string,
) {
    try {
        if (locale == storeDefaultLocale) return lineItems;
        for (let i = 0; i < lineItems.length; i++) {
            lineItems[i].title =
                (await titleTranslation(
                    storeDefaultLocale,
                    shopifyDomain,
                    locale,
                    lineItems[i].product_handle,
                    localeRoute,
                )) || lineItems[i].title;
            lineItems[i].variant_title =
                (await variantTranslation(
                    storeDefaultLocale,
                    shopifyDomain,
                    locale,
                    lineItems[i]?.product_handle,
                    lineItems[i]?.variant_title,
                    localeRoute,
                )) || lineItems[i]?.variant_title;
        }
        return lineItems;
    } catch (error) {
        return lineItems;
    }
}

export async function transformMultilingualDataForAddProducts(
    data: any,
    shopifyDomain: string,
    localeRoute: string,
    currentLocale: string,
    storeDefaultLocale: string | null,
) {
    try {
        let productHandle = data?.variants[0]?.product_handle;
        if (currentLocale && productHandle && storeDefaultLocale) {
            const url = `https://${shopifyDomain}/${currentLocale.split("-")[0]}/products/${productHandle}`;
            const response = await axios.get(url, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });
            if (!response?.data) return data;
            data.product_title = response.data.product?.title;
            for (let i = 0; i < data.product_options.length; i++) {
                data.product_options[i].name = response.data.product.options[i].name;
                for (let j = 0; j < data.product_options[i].values.length; j++) {
                    data.product_options[i].values[j] = response.data.product.options[i].values[j];
                }
            }
            for (let i = 0; i < data.variants.length; i++) {
                data.variants[i].variant_title = response.data.product.variants[i].title;
            }
        }
    } catch (error) {
        return data;
    }
}

export async function transformMultilingualDataForVariantsForSwap(
    data: any,
    shopifyDomain: string,
    localeRoute: string,
    currentLocale: string,
    storeDefaultLocale: string | null,
) {
    try {
        let productHandle = data?.all_variants[0]?.product_handle;
        if (currentLocale && productHandle && storeDefaultLocale) {
            const url = `https://${shopifyDomain}/${currentLocale.split("-")[0]}/products/${productHandle}`;
            const response = await axios.get(url, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });

            if (!response?.data) return data;
            for (let i = 0; i < data.product_options.length; i++) {
                data.product_options[i].name = response.data.product.options[i].name;
                for (let j = 0; j < data.product_options[i].values.length; j++) {
                    data.product_options[i].values[j] = response.data.product.options[i].values[j];
                }
            }

            const variantTranslations = new Map();
            for (let i = 0; i < data.all_variants.length; i++) {
                variantTranslations.set(data.all_variants[i].variant_title, response.data.product.variants[i].title);
                data.all_variants[i].variant_title = response.data.product.variants[i].title;
            }

            for (let i = 0; i < data.variants.length; i++) {
                data.variants[i].variant_title = variantTranslations.get(data.variants[i].variant_title);
            }
        }
    } catch (error) {
        return data;
    }
}

export async function transformMultilingualDataForVariantsForSwapForEditLine(
    data: any,
    shopifyDomain: string,
    localeRoute: string,
    currentLocale: string,
    storeDefaultLocale: string | null,
) {
    try {
        let productHandle = data?.product_handle;
        if (currentLocale && productHandle) {
            const url = `https://${shopifyDomain}/${currentLocale.split("-")[0]}/products/${productHandle}`;
            const response = await axios.get(url, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });
            if (!response?.data) return data;
            for (let i = 0; i < data.product_options.length; i++) {
                data.product_options[i].name = response.data.product.options[i].name;
                for (let j = 0; j < data.product_options[i].values.length; j++) {
                    data.product_options[i].values[j] = response.data.product.options[i].values[j];
                }
            }
        }
    } catch (error) {
        return data;
    }
}

async function handleMultilingualData(
    data: any,
    shopifyDomain: string,
    currentLocale: string,
    storeDefaultLocale: string | null,
    localeRoute: string,
) {
    const updatedProducts = [];
    const productBatches = [];
    const batchSize = 100;
    for (let i = 0; i < data.length; i += batchSize) {
        productBatches.push(data.slice(i, i + batchSize));
    }
    for (const batch of productBatches) {
        const products = await getProductTranslations(
            batch,
            shopifyDomain,
            currentLocale,
            false,
            localeRoute,
            storeDefaultLocale,
        );
        updatedProducts.push(...products);
    }
    return updatedProducts;
}

async function variantTranslation(
    storeDefaultLocale: string | null,
    shopifyDomain: string,
    locale: string,
    product_handle: string,
    variant_title: string,
    localeRoute: string,
) {
    try {
        if (variant_title == null) return variant_title;
        const translatedData = fetchTranslationDataVariant(
            locale,
            TRANSFORMATION_TYPES.VARIANT,
            product_handle,
            variant_title.replace(/\s/g, ""),
        );
        if (translatedData) return translatedData;
        let varPos: number;

        if (product_handle && variant_title && locale && storeDefaultLocale != locale && storeDefaultLocale != null) {
            let urlforPos = `https://${shopifyDomain}/products/${product_handle}`;
            const res = await axios.get(urlforPos, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });

            if (res && res.data) {
                varPos = res?.data?.product?.variants.findIndex((variant: any) => variant.title == variant_title);
                if (storeDefaultLocale != locale && product_handle) {
                    const url = `https://${shopifyDomain}/${locale.split("-")[0]}/products/${product_handle}`;
                    let response = await axios.get(url, {
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    });
                    const variant_title_translated: string =
                        response?.data?.product?.variants[varPos]?.title || variant_title;
                    setTranslationDataVariant(
                        locale,
                        TRANSFORMATION_TYPES.VARIANT,
                        product_handle,
                        variant_title.replace(/\s/g, ""),
                        variant_title_translated,
                    );
                    return variant_title_translated;
                }
            }
        }
        return variant_title;
    } catch (error) {
        return variant_title;
    }
}

async function titleTranslation(
    storeDefaultLocale: string | null,
    shopifyDomain: string,
    locale: string,
    product_handle: string,
    localeRoute: string,
) {
    try {
        const translatedData = fetchTranslationDataVariant(locale, TRANSFORMATION_TYPES.TITLE, product_handle, "");
        if (translatedData) return translatedData;

        let response: any;
        if (storeDefaultLocale != locale && product_handle && storeDefaultLocale != null && locale) {
            const url = `https://${shopifyDomain}/${locale.split("-")[0]}/products/${product_handle}`;
            response = await axios.get(url, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });

            response["data"] = response?.data;
            setTranslationDataVariant(
                locale,
                TRANSFORMATION_TYPES.TITLE,
                product_handle,
                "",
                response?.data?.product?.title,
            );
        }
        return response?.data?.product?.title;
    } catch (error) {
        return null;
    }
}

async function titleTranslationBundle(locale: string, bundleId: number, shopifyDomain: string) {
    try {
        const bundleIdString = bundleId ? bundleId.toString() : "";
        const translatedData = fetchTranslationData(locale, TRANSFORMATION_TYPES.TITLE, bundleIdString);
        if (translatedData) return translatedData;
        const params = {
            bundleId: bundleId,
            locale: locale || "en",
            shopifyDomain: shopifyDomain,
        };
        const url = `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/bundle/bundlePageTranslationsByID`;
        const response = await axios.get(url, {
            params,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        });

        const title_translated: string = response?.data?.data?.bundle_title;
        return title_translated;
    } catch (error) {
        return null;
    }
}

async function getProductTranslations(
    products: any,
    shopifyDomain: string,
    currentLocale: string,
    usingVariantsTranslation: boolean = false,
    localeRoute: string,
    storeDefaultLocale: string | null,
) {
    const allProducts = products.map(async (product: any) => {
        const translatedTitleData = fetchTranslationData(
            currentLocale,
            TRANSFORMATION_TYPES.TITLE,
            product?.product_handle,
        );
        if (!translatedTitleData || translatedTitleData == "") {
            if (
                product?.product_handle &&
                currentLocale &&
                storeDefaultLocale != currentLocale &&
                storeDefaultLocale != null
            ) {
                const url = `https://${shopifyDomain}/${currentLocale.split("-")[0]}/products/${product.product_handle}`;
                let response: any;
                try {
                    response = await axios.get(url, {
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    });
                } catch (error) {
                    response = { data: { product: { title: product?.product_title } } };
                }
                setTranslationData(currentLocale, TRANSFORMATION_TYPES.TITLE, product.product_handle, response.data);
                return {
                    title: response?.data?.product?.title,
                };
            } else {
                const bundleId = product.bundleId;
                let title_translated = "";
                let response: { data: any } = { data: undefined };
                if (bundleId) {
                    const url = `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/bundle/bundlePageTranslationsByID`;
                    const params = {
                        bundleId: bundleId,
                        locale: currentLocale || "en",
                        shopifyDomain: shopifyDomain,
                    };
                    response = await axios.get(url, {
                        params,
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    });
                }
                title_translated = response?.data?.data?.bundle_title;
                return {
                    title: title_translated || product?.product_title,
                };
            }
        } else {
            if (!usingVariantsTranslation) {
                return {
                    title: translatedTitleData,
                };
            }
            return {
                title: translatedTitleData,
            };
        }
    });
    // Return the resolved promise
    return Promise.all(allProducts);
}

export const areDatesEqual = (d1: any, d2: any) => {
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
};

/* This function is currently only used for fetching images of the product, varinats and body_html of the product,
let it remain fetchingData using localeRoute only and don't use locale and storeDefaultLocale or (storeDefaultLocale != currentLocale) logic here
*/
export async function fetchProductDataFromShopify(product_handle: string, shopifyDomain: string, localeRoute: string) {
    try {
        if (product_handle) {
            let url: string = `https://${shopifyDomain}/${localeRoute.split("-")[0]}/products/${product_handle}.json`;
            const response = await axios.get(url);
            return response.data;
        }
        return null;
    } catch (error) {
        return null;
    }
}

export async function getUpdatePMSessionId(body: any) {
    const response = await axios.post("https://deposit.shopifycs.com/sessions", body, { headers: { "Content-Type": "application/json" } });
    return response.data;
}

export function findMinAmount(...amounts: (number | null | undefined)[]): number | null {
    const validAmounts = amounts.filter(
        (amount): amount is number => amount !== null && amount !== undefined && !isNaN(Number(amount)),
    );

    if (validAmounts.length === 0) {
        return null;
    }

    return Math.min(...validAmounts);
}

// Function to set the storefrontBannerSession
export function setStorefrontBannerSession() {
    const currentTime = new Date().getTime(); // Get current time in milliseconds
    const sessionExpirationTime = currentTime + 24 * 60 * 60 * 1000; // Add 24 hours to the current time

    // Check if storefrontBannerSession already exists in localStorage
    if (!safeStorage.getItem("storefrontBannerSession")) {
        // If not, create a new session with expiration time
        safeStorage.setItem("storefrontBannerSession", JSON.stringify({ expirationTime: sessionExpirationTime }));
    }
}

// Function to check if the storefrontBannerSession has expired
export function isStorefrontBannerSessionExpired() {
    const storedSession = safeStorage.getItem("storefrontBannerSession");

    // If no session is found, return true (meaning session is not valid)
    if (!storedSession) {
        return true; // No session means it has expired or not set
    }

    const { expirationTime } = JSON.parse(storedSession);
    const currentTime = new Date().getTime();

    // If the current time is greater than the expiration time, session has expired
    if (currentTime >= expirationTime) {
        safeStorage.removeItem("storefrontBannerSession");
        return true; // Session expired
    }

    // Otherwise, the session is still valid
    return false; // Session still valid
}

export function addDelay(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

export async function addUpgradeLog(axios, body) {
    const res = await axios.post(`${process.env.NEXT_PUBLIC_API_SERVICE}/subscriptionUpgrade/profile/view`, body);
}

export async function translateUpgradeProfileProducts(
    profiles: any,
    locale: any,
    defaultLocale: any,
    shopifyDomain: any,
) {
    if (locale === defaultLocale) {
        return profiles;
    }

    const _profileData = profiles.slice();

    // Use Promise.all for the outer loop as well to parallelize profile processing
    await Promise.all(
        _profileData.map(async (profile) => {
            const existingProductHandle = profile?.existingProductInfo?.productHandle;
            const existingVariantTitle = profile?.existingProductInfo?.variantTitle;

            // Parallelize existing product translations
            const [translatedProductTitleEx, translatedVariantTitleEx] = await Promise.all([
                titleTranslation(defaultLocale, shopifyDomain, locale, existingProductHandle, ""),
                variantTranslation(
                    defaultLocale,
                    shopifyDomain,
                    locale,
                    existingProductHandle,
                    existingVariantTitle,
                    "",
                ),
            ]);

            profile.existingProductInfo.name = translatedProductTitleEx && translatedVariantTitleEx
                ? `${translatedProductTitleEx}${translatedVariantTitleEx ? ` - ${translatedVariantTitleEx}` : ""}`
                : translatedProductTitleEx && !translatedVariantTitleEx ? translatedProductTitleEx : profile.existingProductInfo.name;

            // Parallelize all updated product translations
            const updatedProductTranslations = await Promise.all(
                profile.updatedProductInfo.map(async (updatedProduct: any) => {
                    const updatedProductHandle = updatedProduct?.productHandle;
                    const updatedVariantTitle = updatedProduct?.variantTitle;

                    const [translatedProductTitleCur, translatedVariantTitleCur] = await Promise.all([
                        titleTranslation(defaultLocale, shopifyDomain, locale, updatedProductHandle, ""),
                        variantTranslation(
                            defaultLocale,
                            shopifyDomain,
                            locale,
                            updatedProductHandle,
                            updatedVariantTitle,
                            "",
                        ),
                    ]);

                    return {
                        translatedProductTitleCur,
                        translatedVariantTitleCur,
                    };
                }),
            );

            // Apply the translations back to the original objects
            updatedProductTranslations.forEach((translation, index) => {
                const { translatedProductTitleCur, translatedVariantTitleCur } = translation;
                profile.updatedProductInfo[index].name =
                    translatedProductTitleCur && translatedVariantTitleCur
                        ? `${translatedProductTitleCur}${translatedVariantTitleCur ? ` - ${translatedVariantTitleCur}` : ""}`
                        : translatedProductTitleCur && !translatedVariantTitleCur
                            ? translatedProductTitleCur
                            : profile.updatedProductInfo[index].name;
            });
        }),
    );

    return _profileData;
}

export function getCustomerAuthToken(router: NextRouter) {
    let token = safeStorage.getItem(`loopAccessToken`);
    if (router.query.merchantToken) {
        token = router.query.merchantToken as string;
    }
    return `Bearer ${token}`;
}


export function getFinalPriceAfterLoyaltyDiscountBh(price: number, minRedeemablePoints: number, maxRedeemablePoints: number, points: number, availableBuckets: number[], pointsConversionRate: number, isLoading: boolean) {
    const minRedeemablePointsDiscount = minRedeemablePoints ? minRedeemablePoints * (Number(pointsConversionRate)) : 0;
    if (points && points >= minRedeemablePoints && price >= minRedeemablePointsDiscount) {
        if (!isLoading) {
            let minPointCheck = minRedeemablePoints ? minRedeemablePoints : 0;
            let maxPointCheck = maxRedeemablePoints ? maxRedeemablePoints : Number.MAX_SAFE_INTEGER;

            if (availableBuckets && availableBuckets.length) {
                let maxBucketSize = 0;
                for (const ele of availableBuckets) {
                    if (ele > maxBucketSize && (ele <= maxPointCheck && ele >= minPointCheck)) {
                        maxBucketSize = ele;
                    }
                }
                const maxPointsToBeRedeemed = maxBucketSize <= points ? maxBucketSize : points;
                const discountedPriceForLoyalty = maxPointsToBeRedeemed * (Number(pointsConversionRate));
                let remainingPoints = points - maxPointsToBeRedeemed;
                let finalPrice = price - discountedPriceForLoyalty;
                if (finalPrice < 0) {
                    remainingPoints = Math.abs(finalPrice) / pointsConversionRate;
                    finalPrice = 0;
                } else {
                    remainingPoints = 0;
                }
                return {
                    discountedPrice: finalPrice,
                    isDiscounted: true,
                    maxPointsToBeRedeemed,
                    remainingPoints,
                    discountAmount: price < discountedPriceForLoyalty ? price : discountedPriceForLoyalty,
                    pointsConversionRate: Number(pointsConversionRate),
                    loyaltyProvider: Integrations.BUBBLEHOUSE,
                    discountType: LoopDiscountType.FIXED_AMOUNT,
                    discountPercentage: 0
                };
            } else {
                const maxPointsToBeRedeemed = maxPointCheck <= points ? maxRedeemablePoints : points;
                const discountedPriceForLoyalty = maxPointsToBeRedeemed * (Number(pointsConversionRate));
                let remainingPoints = points - maxPointsToBeRedeemed;
                let finalPrice = price - discountedPriceForLoyalty;
                if (finalPrice <= 0) {
                    remainingPoints = Math.abs(finalPrice) / pointsConversionRate;
                    finalPrice = 0;
                } else {
                    remainingPoints = 0;
                }
                return {
                    discountedPrice: finalPrice,
                    isDiscounted: true,
                    maxPointsToBeRedeemed,
                    remainingPoints,
                    discountAmount: price < discountedPriceForLoyalty ? price : discountedPriceForLoyalty,
                    pointsConversionRate: Number(pointsConversionRate),
                    loyaltyProvider: Integrations.BUBBLEHOUSE,
                    discountType: LoopDiscountType.FIXED_AMOUNT,
                    discountPercentage: 0
                };
            }
        }
    }
    return {
        discountedPrice: price,
        isDiscounted: false,
        maxPointsToBeRedeemed: 0,
        discountAmount: 0,
    };
}

export function getFinalPriceAfterLoyaltyDiscountYotpo(
    price: number,
    points: number,
    redeemRule: any,
    isLoading: boolean,
    contractPrice: number,
    quantity: number,
) {
    if (!isLoading) {
        let applicableRedeemRule = null;
        const sortedRedeemRule = redeemRule.sort((a, b) => b.amount - a.amount);
        if (sortedRedeemRule && sortedRedeemRule.length > 0) {
            for (const rule of sortedRedeemRule) {
                const minCartValue = rule.minimumCartValue
                    ? Number(rule.minimumCartValue)
                    : 0;
                if (points > rule.amount && Number(contractPrice) >= minCartValue) {
                    applicableRedeemRule = rule;
                    break;
                }
            }
        }
        let eligibleQuantity = quantity;
        if (applicableRedeemRule) {
            while (eligibleQuantity > 0) {
                if (applicableRedeemRule.amount * eligibleQuantity <= points) {
                    break;
                }
                eligibleQuantity--;
            }
            let discountedPriceForLoyalty = 0;
            let finalPriceSingle = price;
            let finalPrice = price * quantity;
            if (applicableRedeemRule.discountType === "fixed_amount") {
                discountedPriceForLoyalty =
                    eligibleQuantity * (applicableRedeemRule.discountFixedCents / 100);
                finalPriceSingle = price - (applicableRedeemRule.discountFixedCents / 100);
            } else if (applicableRedeemRule.discountType === "percentage") {
                discountedPriceForLoyalty =
                    eligibleQuantity * ((applicableRedeemRule.discountPercentage / 100) * price);
                finalPriceSingle = price - ((applicableRedeemRule.discountPercentage / 100) * price);
            }
            finalPrice = (price * quantity) - discountedPriceForLoyalty;
            let remainingPoints = (points - (applicableRedeemRule.amount) * (eligibleQuantity));
            if (finalPrice <= 0) {
                finalPrice = 0;
            }
            if (price * quantity < discountedPriceForLoyalty) {
                discountedPriceForLoyalty = price * quantity;
            }
            if (finalPriceSingle < 0) {
                finalPriceSingle = 0;
            }
            return {
                discountedPrice: finalPriceSingle,
                isDiscounted: true,
                maxPointsToBeRedeemed: points,
                discountAmount: discountedPriceForLoyalty,
                remainingPoints,
                loyaltyProvider: Integrations.YOTPO_LOYALTY,
                discountType: applicableRedeemRule.discountType === "percentage" ? LoopDiscountType.PERCENTAGE : LoopDiscountType.FIXED_AMOUNT,
                discountPercentage: applicableRedeemRule.discountPercentage
            };
        }
        return {
            discountedPrice: price,
            isDiscounted: false,
            maxPointsToBeRedeemed: 0,
            discountAmount: 0,
        };
    }
    return {
        discountedPrice: price,
        isDiscounted: false,
        maxPointsToBeRedeemed: 0,
        discountAmount: 0,
    };
}

export function getFinalPriceAfterLoyaltyDiscountInfluence(
    price: number,
    points: number,
    redeemRule: any,
    isLoading: boolean,
    quantity: number
) {
    if (!isLoading) {
        let applicableRedeemRule = null;
        const sortedRedeemRule = redeemRule.sort((a, b) => b.pointCost - a.pointCost);
        if (sortedRedeemRule && sortedRedeemRule.length > 0) {
            for (const rule of sortedRedeemRule) {
                if (points > rule.pointCost) {
                    applicableRedeemRule = rule;
                    break;
                }
            }
        }
        let eligibleQuantity = quantity;
        if (applicableRedeemRule) {
            while (eligibleQuantity > 0) {
                if (applicableRedeemRule.pointCost * eligibleQuantity <= points) {
                    break;
                }
                eligibleQuantity--;
            }
            let discountedPriceForLoyalty = 0;
            let finalPriceSingle = price;
            let finalPrice = price * quantity;
            if (applicableRedeemRule.discountType === LoopDiscountType.FIXED_AMOUNT) {
                discountedPriceForLoyalty =
                    eligibleQuantity * (applicableRedeemRule.discountValue);
                finalPriceSingle = price - (applicableRedeemRule.discountValue);
            } else if (applicableRedeemRule.discountType === LoopDiscountType.PERCENTAGE) {
                discountedPriceForLoyalty =
                    eligibleQuantity * ((applicableRedeemRule.discountValue / 100) * price);
                finalPriceSingle = price - ((applicableRedeemRule.discountValue / 100) * price);
            }
            finalPrice = (price * quantity) - discountedPriceForLoyalty;
            let remainingPoints = (points - (applicableRedeemRule.pointCost) * (eligibleQuantity));
            if (finalPrice < 0) {
                finalPrice = 0;
            }
            if (price * quantity < discountedPriceForLoyalty) {
                discountedPriceForLoyalty = price * quantity;
            }
            if (finalPriceSingle < 0) {
                finalPriceSingle = 0;
            }
            return {
                discountedPrice: finalPriceSingle,
                isDiscounted: true,
                maxPointsToBeRedeemed: points,
                discountAmount: discountedPriceForLoyalty,
                remainingPoints,
                loyaltyProvider: Integrations.INFLUENCE,
                discountType: applicableRedeemRule.discountType,
                discountPercentage: applicableRedeemRule.discountType === LoopDiscountType.PERCENTAGE ? applicableRedeemRule.discountValue : null
            };
        }
        return {
            discountedPrice: price,
            isDiscounted: false,
            maxPointsToBeRedeemed: 0,
            discountAmount: 0,
        };
    }
    return {
        discountedPrice: price,
        isDiscounted: false,
        maxPointsToBeRedeemed: 0,
        discountAmount: 0,
    };
}

export function getFinalPriceAfterLoyaltyDiscountRivo(
    price: number,
    points: number,
    redeemRule: any,
    isLoading: boolean,
    quantity: number
) {
    if (!isLoading) {
        let applicableRedeemRule = null;
        const sortedRedeemRule = redeemRule.sort((a, b) => b.pointCost - a.pointCost);
        if (sortedRedeemRule && sortedRedeemRule.length > 0) {
            for (const rule of sortedRedeemRule) {
                if (points > rule.pointCost) {
                    applicableRedeemRule = rule;
                    break;
                }
            }
        }
        let eligibleQuantity = quantity;
        if (applicableRedeemRule) {
            while (eligibleQuantity > 0) {
                if (applicableRedeemRule.pointCost * eligibleQuantity <= points) {
                    break;
                }
                eligibleQuantity--;
            }
            let discountedPriceForLoyalty = 0;
            let finalPriceSingle = price;
            let finalPrice = price * quantity;
            if (applicableRedeemRule.discountType === LoopDiscountType.FIXED_AMOUNT) {
                discountedPriceForLoyalty =
                    eligibleQuantity * (applicableRedeemRule.rewardValue);
                finalPriceSingle = price - (applicableRedeemRule.rewardValue);
            } else if (applicableRedeemRule.discountType === LoopDiscountType.PERCENTAGE) {
                discountedPriceForLoyalty =
                    eligibleQuantity * ((applicableRedeemRule.rewardValue / 100) * price);
                finalPriceSingle = price - ((applicableRedeemRule.rewardValue / 100) * price);
            }

            finalPrice = (price * quantity) - discountedPriceForLoyalty;
            let remainingPoints = (points - (applicableRedeemRule.pointCost) * (eligibleQuantity));
            if (finalPrice < 0) {
                finalPrice = 0;
            }
            if (price * quantity < discountedPriceForLoyalty) {
                discountedPriceForLoyalty = price * quantity;
            }
            if (finalPriceSingle < 0) {
                finalPriceSingle = 0;
            }
            return {
                discountedPrice: finalPriceSingle,
                isDiscounted: true,
                maxPointsToBeRedeemed: points,
                discountAmount: discountedPriceForLoyalty,
                remainingPoints,
                loyaltyProvider: Integrations.RIVO,
                discountType: applicableRedeemRule.discountType,
                discountPercentage: applicableRedeemRule.discountType === LoopDiscountType.PERCENTAGE ? applicableRedeemRule.rewardValue : null
            };
        }
        return {
            discountedPrice: price,
            isDiscounted: false,
            maxPointsToBeRedeemed: 0,
            discountAmount: 0,
        };
    }
    return {
        discountedPrice: price,
        isDiscounted: false,
        maxPointsToBeRedeemed: 0,
        discountAmount: 0,
    };
}

export function getFinalPriceAfterLoyaltyDiscountOkendo(
    price: number,
    points: number,
    redeemRule: any,
    isLoading: boolean,
    contractPrice: number,
    quantity: number
) {
    if (!isLoading) {
        let applicableRedeemRule = null;
        const sortedRedeemRule = redeemRule.sort((a, b) => b.points - a.points);
        if (sortedRedeemRule && sortedRedeemRule.length > 0) {
            for (const rule of sortedRedeemRule) {
                const minimumPurchase = rule.minimumPurchase
                    ? Number(rule.minimumPurchase)
                    : 0;
                if (points > rule.points && Number(contractPrice) >= minimumPurchase) {
                    applicableRedeemRule = rule;
                    break;
                }
            }
        }
        let eligibleQuantity = quantity;
        if (applicableRedeemRule) {
            while (eligibleQuantity > 0) {
                if (applicableRedeemRule.points * eligibleQuantity <= points) {
                    break;
                }
                eligibleQuantity--;
            }
            let discountedPriceForLoyalty = 0;
            let finalPriceSingle = price;
            let finalPrice = price * quantity;
            if (applicableRedeemRule.loopDiscountType === LoopDiscountType.FIXED_AMOUNT) {
                discountedPriceForLoyalty =
                    eligibleQuantity * (applicableRedeemRule.value);
                finalPriceSingle = price - (applicableRedeemRule.value);
            } else if (applicableRedeemRule.loopDiscountType === LoopDiscountType.PERCENTAGE) {
                discountedPriceForLoyalty =
                    eligibleQuantity * ((applicableRedeemRule.value / 100) * price);
                finalPriceSingle = price - ((applicableRedeemRule.value / 100) * price);
            }

            finalPrice = (price * quantity) - discountedPriceForLoyalty;
            let remainingPoints = (points - (applicableRedeemRule.points) * (eligibleQuantity));
            if (finalPrice < 0) {
                finalPrice = 0;
            }
            if (price * quantity < discountedPriceForLoyalty) {
                discountedPriceForLoyalty = price * quantity;
            }
            if (finalPriceSingle < 0) {
                finalPriceSingle = 0;
            }
            return {
                discountedPrice: finalPriceSingle,
                isDiscounted: true,
                maxPointsToBeRedeemed: points,
                discountAmount: discountedPriceForLoyalty,
                remainingPoints,
                loyaltyProvider: Integrations.OKENDO,
                discountType: applicableRedeemRule.loopDiscountType,
                discountPercentage:
                    applicableRedeemRule.loopDiscountType === LoopDiscountType.PERCENTAGE
                        ? applicableRedeemRule.value
                        : null,
            };
        }
        return {
            discountedPrice: price,
            isDiscounted: false,
            maxPointsToBeRedeemed: 0,
            discountAmount: 0,
        };
    }
    return {
        discountedPrice: price,
        isDiscounted: false,
        maxPointsToBeRedeemed: 0,
        discountAmount: 0,
    };
}

export function getFinalPriceAfterLoyaltyDiscountRiseAi(
    price: number,
    credits: number,
    isLoading: boolean,
) {
    if (!isLoading) {
        if (credits > 0) {
            const maxPointsToBeRedeemed = credits;
            const discountedPriceForLoyalty = price < credits ? price : credits;
            let remainingPoints = credits - discountedPriceForLoyalty;
            let finalPrice = price - discountedPriceForLoyalty;

            return {
                discountedPrice: finalPrice,
                isDiscounted: true,
                maxPointsToBeRedeemed,
                remainingPoints,
                discountAmount: discountedPriceForLoyalty,
                loyaltyProvider: Integrations.RISEAI,
                discountType: LoopDiscountType.FIXED_AMOUNT,
                discountPercentage: 0
            };
        }
    }
    return {
        discountedPrice: price,
        isDiscounted: false,
        maxPointsToBeRedeemed: 0,
        discountAmount: 0,
    };
}

export enum Integrations {
    BUBBLEHOUSE = "bubblehouse",
    YOTPO_LOYALTY = "yotpo-loyalty",
    INFLUENCE = "influence-io",
    RIVO = "rivo",
    OKENDO = "okendo",
    RISEAI = "rise-ai",
}